"use client";

import React, { useCallback, useEffect, useState } from "react";
import { LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";

import type { ContactDictionary } from "@/content/home";

interface ContactProps {
  copy: ContactDictionary;
}

interface ContactFormContentProps {
  copy: ContactDictionary;
}

function ContactFormContent({ copy }: ContactFormContentProps) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isSending, setIsSending] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  const handleFormSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const formElement = event.currentTarget;

      if (!executeRecaptcha) {
        return;
      }

      setIsSending(true);

      try {
        const token = await executeRecaptcha("contact_form");
        const formData = new FormData(formElement);

        const data = {
          fullName: formData.get("fullName"),
          company: formData.get("company"),
          position: formData.get("position"),
          email: formData.get("email"),
          phone: `${formData.get("prefix")} ${formData.get("phone")}`,
          service: formData.get("service"),
          brief: formData.get("brief"),
          captchaToken: token,
        };

        const response = await fetch("/api/send", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Error sending the form");
        }

        setIsSubmitted(true);
        formElement.reset();
      } catch (error) {
        console.error("Error sending contact form:", error);
        alert(error instanceof Error ? error.message : "A technical error occurred. Please try again.");
      } finally {
        setIsSending(false);
      }
    },
    [executeRecaptcha],
  );

  if (isSubmitted) {
    return (
      <div className="lg:col-span-8 min-h-[400px] flex items-start">
        <m.div
          role="alert"
          initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="w-full py-12 flex flex-col items-start"
        >
          <div className="w-16 h-16 bg-[#E65028]/10 flex items-center justify-center rounded-full mb-8">
            <svg className="w-8 h-8 text-[#E65028]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-[#0A111F] text-3xl font-bold mb-4 uppercase">{copy.successTitle}</h3>
          <p className="text-gray-600 text-lg max-w-md leading-relaxed mb-10">{copy.successBody}</p>
          <button
            onClick={() => setIsSubmitted(false)}
            className="text-[#E65028] text-[10px] uppercase tracking-[0.2em] font-bold border-b border-[#E65028] pb-1 hover:text-[#0A111F] hover:border-[#0A111F] transition-all"
          >
            {copy.successAction}
          </button>
        </m.div>
      </div>
    );
  }

  return (
    <div className="lg:col-span-8 min-h-[400px] flex items-start">
      <m.form
        onSubmit={handleFormSubmit}
        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10 w-full"
      >
        <div className="flex flex-col border-b border-gray-200 focus-within:border-[#0A111F] transition-colors">
          <label htmlFor="fullName" className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{copy.form.fullName}</label>
          <input id="fullName" name="fullName" required type="text" className="pb-4 bg-transparent outline-none text-gray-800 text-sm font-medium" placeholder={copy.placeholders.fullName} />
        </div>

        <div className="flex flex-col border-b border-gray-200 focus-within:border-[#0A111F] transition-colors">
          <label htmlFor="company" className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{copy.form.company}</label>
          <input id="company" name="company" required type="text" className="pb-4 bg-transparent outline-none text-gray-800 text-sm font-medium" />
        </div>

        <div className="flex flex-col border-b border-gray-200 focus-within:border-[#0A111F] transition-colors">
          <label htmlFor="position" className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{copy.form.position}</label>
          <input id="position" name="position" type="text" className="pb-4 bg-transparent outline-none text-gray-800 text-sm font-medium" placeholder={copy.placeholders.position} />
        </div>

        <div className="flex flex-col border-b border-gray-200 focus-within:border-[#0A111F] transition-colors relative">
          <label htmlFor="email" className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{copy.form.email}</label>
          <input
            id="email"
            name="email"
            required
            type="email"
            className="pb-4 bg-transparent outline-none text-gray-800 text-sm font-medium"
          />
        </div>

        <div className="flex flex-col border-b border-gray-200 focus-within:border-[#0A111F] transition-colors">
          <label htmlFor="phone" className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{copy.form.phone}</label>
          <div className="flex gap-2">
            <select
              id="prefix"
              name="prefix"
              aria-label={copy.form.countryCode}
              className="pb-4 bg-transparent outline-none text-gray-500 text-sm appearance-none cursor-pointer border-r border-gray-100 pr-2"
            >
              <option value="+1">USA (+1)</option>
              <option value="+58">VEN (+58)</option>
              <option value="+592">GUY (+592)</option>
              <option value="+57">COL (+57)</option>
              <option value="+52">MEX (+52)</option>
              <option value="+593">ECU (+593)</option>
              <option value="+507">PAN (+507)</option>
              <option value="+54">ARG (+54)</option>
            </select>
            <input
              id="phone"
              name="phone"
              placeholder={copy.placeholders.phone}
              required
              type="tel"
              className="pb-4 bg-transparent outline-none text-gray-800 text-sm font-medium w-full"
            />
          </div>
        </div>

        <div className="flex flex-col border-b border-gray-200 focus-within:border-[#0A111F] transition-colors">
          <label htmlFor="service" className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{copy.form.service}</label>
          <select id="service" name="service" required className="pb-4 bg-transparent outline-none text-gray-800 text-sm appearance-none cursor-pointer font-medium">
            <option value="">{copy.form.selectService}</option>
            {copy.serviceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2 flex flex-col border-b border-gray-200 focus-within:border-[#0A111F] transition-colors">
          <label htmlFor="brief" className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">{copy.form.brief}</label>
          <textarea
            id="brief"
            name="brief"
            required
            maxLength={500}
            className="pb-4 bg-transparent outline-none text-gray-800 text-sm h-32 resize-none font-medium"
            placeholder={copy.placeholders.brief}
          />
        </div>

        <div className="md:col-span-2 flex flex-col sm:flex-row items-center justify-between gap-6">
          <button
            type="submit"
            disabled={isSending || !executeRecaptcha}
            className={`bg-[#0A111F] text-white w-full sm:w-auto px-12 py-5 text-[11px] uppercase tracking-[0.3em] font-bold transition-all duration-300 flex items-center justify-center gap-3 ${(isSending || !executeRecaptcha) ? "opacity-70 cursor-not-allowed" : "hover:bg-[#E65028]"}`}
          >
            {isSending ? copy.form.sending : !executeRecaptcha ? copy.form.securing : copy.form.submit}
          </button>
          <span className="text-[9px] text-gray-500 uppercase tracking-widest text-center sm:text-right">
            {copy.form.privacy} <br />
            {copy.form.privacyLineTwo}
          </span>
        </div>
      </m.form>
    </div>
  );
}

export default function Contact({ copy }: ContactProps) {
  const [shouldLoadCaptcha, setShouldLoadCaptcha] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const handleInteraction = () => {
      setShouldLoadCaptcha(true);
      ["mouseover", "keydown", "touchstart", "scroll"].forEach((eventName) =>
        window.removeEventListener(eventName, handleInteraction),
      );
    };

    ["mouseover", "keydown", "touchstart", "scroll"].forEach((eventName) =>
      window.addEventListener(eventName, handleInteraction, { passive: true }),
    );

    return () => {
      ["mouseover", "keydown", "touchstart", "scroll"].forEach((eventName) =>
        window.removeEventListener(eventName, handleInteraction),
      );
    };
  }, []);

  const content = (
    <LazyMotion features={domAnimation}>
      <section id="contacto" className="bg-white py-24 lg:py-32 font-montserrat">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <m.div
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-20"
          >
            <h2 className="text-[#0A111F] text-4xl lg:text-5xl font-light tracking-tight mb-6 uppercase">
              {copy.heading} <span className="font-bold">{copy.headingAccent}</span>
            </h2>
            <div className="w-20 h-1 bg-[#E65028]" />
          </m.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-20">
            <m.div
              initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, x: -28 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="lg:col-span-4 space-y-12"
            >
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-4">{copy.headquartersTitle}</h4>
              <p className="text-[#0A111F] text-xl font-medium leading-snug">{copy.headquartersBody}</p>
            </div>

            <div>
              <h4 className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-4">{copy.venezuelaTitle}</h4>
              <p className="text-[#0A111F] text-xl font-medium leading-snug">{copy.venezuelaBody}</p>
            </div>

            <div>
              <h4 className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-4">{copy.inquiriesTitle}</h4>
              <p className="text-[#0A111F] text-xl font-medium">{copy.inquiriesPhone}</p>
              <a href={`mailto:${copy.inquiriesEmail}`} className="text-[#E65028] text-sm hover:underline mt-2 inline-block font-bold">
                {copy.inquiriesEmail}
              </a>
            </div>

            <div className="pt-12 border-t border-gray-100 italic">
              <p className="text-gray-600 text-sm leading-relaxed">{copy.note}</p>
            </div>
            </m.div>

            <ContactFormContent copy={copy} />
          </div>
        </div>
      </section>
    </LazyMotion>
  );

  if (!shouldLoadCaptcha) {
    return content;
  }

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey="6Ldhl7UsAAAAAJ-Qpo-jxm6jUjCy_ddAKwn3ykg1"
      container={{ parameters: { badge: "bottomright" } }}
      scriptProps={{ async: true, defer: true, appendTo: "body" }}
    >
      {content}
    </GoogleReCaptchaProvider>
  );
}
